import { formatHm, formatInt } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { ChannelId, PartyWrapped, WrappedChannel, WrappedDj } from '@mixwire/contracts';
import type { CSSProperties, ReactElement } from 'react';

type Slide06DjsProps = {
  readonly wrapped: PartyWrapped;
  readonly page?: DjPage;
};

export type DjPage = {
  readonly index: number;
  readonly count: number;
  readonly djs: readonly WrappedDj[];
};

const SUMMARY_STYLE: StyleWithVars = { '--d': '1500' };
const FIRST_ROW_DELAY_MS = 250;
const ROW_STAGGER_MS = 140;
const FIRST_FILL_DELAY_MS = 450;
const FULL_PERCENT = 100;

const colourOf = (channels: readonly WrappedChannel[], slotNumber: ChannelId): string =>
  channels.find((channel) => channel.slotNumber === slotNumber)?.colour ?? 'currentcolor';

export const Slide06Djs = ({ wrapped, page }: Slide06DjsProps): ReactElement => {
  const { djs, channels } = wrapped;
  const topDj = djs[0];

  if (topDj === undefined) {
    return (
      <>
        <h2 className="big rise">Who held the decks</h2>
        <p className="lede rise">Nobody claimed a channel for long enough to clock a shift.</p>
      </>
    );
  }

  const maxMs = Math.max(topDj.ms, 1);
  const { index: pageIndex, count: pageCount, djs: pageDjs } = page ?? { index: 0, count: 1, djs };

  return (
    <>
      <h2 className="big rise">
        Who held the decks
        {pageCount > 1 ? (
          <span className="page-of">
            {String(pageIndex + 1)}/{String(pageCount)}
          </span>
        ) : null}
      </h2>
      <ul className="faders">
        {pageDjs.map((dj, index) => {
          const rowStyle: StyleWithVars = {
            '--d': String(FIRST_ROW_DELAY_MS + index * ROW_STAGGER_MS),
          };
          const fillStyle: StyleWithVars = {
            '--d': String(FIRST_FILL_DELAY_MS + index * ROW_STAGGER_MS),
            width: `${String((dj.ms / maxMs) * FULL_PERCENT)}%`,
          };

          return (
            <li className="rise" key={dj.name} style={rowStyle}>
              <span className="who">
                <b>{dj.name}</b>
                <span className="t">{formatHm(dj.ms)}</span>
              </span>
              <span className="fader-track">
                <span className="fill grow" style={fillStyle}>
                  {dj.msBySlot.map((slot) => {
                    const segmentStyle: CSSProperties & StyleWithVars = {
                      '--c': colourOf(channels, slot.slotNumber),
                      flex: slot.ms,
                    };

                    return <i key={slot.slotNumber} style={segmentStyle} />;
                  })}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {pageIndex === 0 ? (
        <p className="lede rise" style={SUMMARY_STYLE}>
          <b className="dj-name">{topDj.name}</b> did the longest shift: {formatHm(topDj.ms)} over{' '}
          {formatInt(topDj.stints)} {topDj.stints === 1 ? 'stint' : 'stints'}.
        </p>
      ) : null}
    </>
  );
};
